import React from 'react';
import PropTypes from 'prop-types';
import { FlexGrid, Dropdown, Header } from '@gasbuddy/react-components';

import Modal from '../modal';
import Button from '../button';
import InputWithLabel from '../input-with-header';
import Notification from '../notification';
import BannerMessage from '../banner-message';
import BPUtils from '../../../lib/bputils';


import { ReplyTemplatePropType } from './prop-types';

const DELETE_TEMPLATE_CONFIRMATION_MESSAGE = 'Are you sure you want to delete this template?';
// eslint-disable-next-line max-len
const DEFAULT_TEMPLATE_BANNER_MESSAGE = 'This is one of Business Page\'s default review reply templates. Feel free to use this as a basis when crafting your own reply templates.';
export default function ReviewReplyModal({ show, template, replyCategories, onSave, onClose, onDelete }) {
  const [category, setCategory] = React.useState(replyCategories[0]);
  const [title, setTitle] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [hasTemplateTitleError, setHasTemplateTitleError] = React.useState(false);
  const [hasTemplateMessageError, setHasTemplateMessageError] = React.useState(false);


  React.useEffect(() => {
    setCategory(template?.category || replyCategories[0]);
    setTitle(template?.title || '');
    setMessage(template?.message || '');
    setHasTemplateTitleError(false);
    setHasTemplateMessageError(false);
  }, [template?.id]);

  const handleOnSave = () => {
    if (!(hasTemplateTitleError || hasTemplateTitleError)) {
      let inputsValid = true;
      if (!title.length) {
        setHasTemplateTitleError(true);
        inputsValid = false;
      }
      if (!message.length) {
        setHasTemplateMessageError(true);
        inputsValid = false;
      }
      if (inputsValid) {
        if (template) {
          updateTemplate({ category, title, message }, template.id);
        } else {
          addTemplate({ category, title, message });
        }
        return true;
      }
    }
    return false;
  };

  const addTemplate = async (newTemplate) => {
    await BPUtils.api.post({
      path: '/reviews/replies/templates',
      requestId: 'saveNewReplyTemplate',
      data: newTemplate,
      success: () => {
        Notification.success('The review reply template was added successfully');
        onSave(newTemplate);
      },
      error: () => {
        Notification.error('Failed to add the review reply template. Please try again.');
      },
    });
  };

  const updateTemplate = async (existingTemplate, templateId) => {
    await BPUtils.api.put({
      path: `/reviews/replies/templates/${templateId}`,
      requestId: 'updateReplyTemplate',
      data: existingTemplate,
      success: () => {
        Notification.success('The review reply template was updated successfully');
        onSave({ id: templateId, ...existingTemplate });
      },
      error: () => {
        Notification.error('Failed to update the review reply template. Please try again.');
      },
    });
  };

  const handleOnClose = () => {
    onClose();
  };

  const deleteTemplate = async () => {
    await BPUtils.api.delete({
      path: `/reviews/replies/templates/${template.id}`,
      requestId: 'deleteReplyTemplate',
      success: () => {
        Notification.success('The template was successfully removed.');
        onDelete(template.id);
      },
      error: () => {
        Notification.error('Failed to remove the review reply template. Please try again.');
      },
    });
  };

  const handleOnReplyCategoryChange = (e) => {
    setCategory(e.currentTarget.value);
  };

  const handleOnTitleChange = (e) => {
    setTitle(e.currentTarget.value);
  };

  const hanleOnMessageChange = (e) => {
    setMessage(e.currentTarget.value);
  };

  const validRequiredInput = (value, errorSetter) => {
    errorSetter(!value.length);
  };

  const validateTitleInput = (e) => {
    validRequiredInput(e.currentTarget.value, setHasTemplateTitleError);
  };

  const validateMessageTextArea = (e) => {
    validRequiredInput(e.currentTarget.value, setHasTemplateMessageError);
  };

  const handleOnModalClose = () => {
    setCategory(replyCategories[0]);
    setTitle('');
    setMessage('');
    setHasTemplateTitleError(false);
    setHasTemplateMessageError(false);
    onClose();
  };

  return (
    <Modal
      size="md"
      show={show}
      // eslint-disable-next-line no-nested-ternary
      title={`${template ? (template.editable ? 'Edit' : 'View') : 'Add'} Reply Template`}
      onClose={handleOnModalClose}
      // eslint-disable-next-line no-nested-ternary
      onSave={(template?.id > 0 || !template) ? handleOnSave : null}
      saveButtonText={(template?.id > 0 || !template) ? `${template?.id > 0 ? 'Update' : 'Save'} Template` : null}
      disableSave={template?.id <= 0}
      onDelete={deleteTemplate}
      deleteButtonText={template?.id > 0 ? 'Delete Template' : null}
      deleteConfirmationMessage={template?.id > 0 ? DELETE_TEMPLATE_CONFIRMATION_MESSAGE : null}
      ctaButtons={(template?.id <= 0 || !template) ? [
        <Button secondary wide onClick={handleOnClose}>Cancel</Button>,
      ] : null}
    >
      {template?.id > 0 && 'ayy lmao'}
      <FlexGrid container>
        {template?.id <= 0 ? (
          <FlexGrid.Column>
            <BannerMessage
              mode="detailed"
              secondaryText={DEFAULT_TEMPLATE_BANNER_MESSAGE}
            />
          </FlexGrid.Column>

        ) : null}
        <FlexGrid.Column mobile={6}>
          <Header size="xs" as="div" snug style={{ paddingTop: '8px', fontWeight: 700 }}>Category</Header>
          <Dropdown
            options={replyCategories.map(cat => ({ value: cat, label: cat }))}
            onChange={handleOnReplyCategoryChange}
            value={category}
            disabled={template?.id <= 0}
          />
        </FlexGrid.Column>
        <FlexGrid.Column mobile={6}>
          <InputWithLabel
            label="Title"
            onChange={handleOnTitleChange}
            editable={!(template?.id <= 0)}
            value={title}
            maxLength={40}
            required
            onBlur={validateTitleInput}
            error={hasTemplateTitleError ? 'Please set a template title' : null}
            isInvalid={hasTemplateTitleError}
          />
        </FlexGrid.Column>
        <FlexGrid.Column mobile={12}>
          <InputWithLabel
            textArea
            label="Message"
            onChange={hanleOnMessageChange}
            editable={!(template?.id <= 0)}
            value={message}
            maxLength={200}
            required
            onBlur={validateMessageTextArea}
            error={hasTemplateMessageError ? 'Please set a template message' : null}
            isInvalid={hasTemplateMessageError}
          />
        </FlexGrid.Column>
      </FlexGrid>
    </Modal>
  );
}

ReviewReplyModal.propTypes = {
  show: PropTypes.bool,
  template: ReplyTemplatePropType,
  replyCategories: PropTypes.arrayOf(PropTypes.string).isRequired,
  onSave: PropTypes.func,
  onClose: PropTypes.func,
  onDelete: PropTypes.func,
};

ReviewReplyModal.defaultProps = {
  show: false,
  template: null,
  onSave: () => {},
  onClose: () => {},
  onDelete: () => {},
};
